import Vue from 'vue'
import { EstimateOld, GlobalSpec, RemoteInvite } from '@models'
import { debounce, flatMap, sumBy, sortBy, cloneDeep } from 'lodash'
import { asNumber, asPositiveRoundedNumber, asRoundedNumber, uuid } from '@utils' 
import { modalStore } from '@stores'

export const estimateStore = {
    state: Vue.observable({
        admin: false,
        estimate: {},
        globalSpecs: [],
        localSpecs: [],
        remoteInvites: [],
    }),

    async init (estimate_id, admin = false) {
        this.state.admin = admin
        await this.fetchEstimate(estimate_id)
        this.checkIfPackageStatusNeedsToBeQuoting()
        await Promise.all([
            this.fetchGlobalSpecs(),
            this.fetchLocalSpecs(),
            this.fetchRemoteInvites(),
        ])
    },

    async fetchEstimate (id) {
        this.state.estimate = await EstimateOld.find(id)
    },

    async fetchGlobalSpecs () {
        this.state.globalSpecs = await GlobalSpec.all({ approved_only: true })
    },

    async fetchLocalSpecs () {
        const localSpecs = await GlobalSpec.all({estimate_id: this.state.estimate.id})
        this.state.localSpecs = sortBy(localSpecs, 'id')
    },

    async fetchRemoteInvites () {
        this.state.remoteInvites = await RemoteInvite.all('estimate', this.state.estimate.id)
    },

    async forceMaterialListRebuild() {
        await EstimateOld.rebuildMaterials(this.state.estimate.id)
    },

    async refreshAndRecalculateLocalSpecs () {
        const updatedLocalSpecs = await GlobalSpec.all({estimate_id: this.state.estimate.id})
        this.state.localSpecs.forEach(localSpec => {
            const updatedLocalSpec = updatedLocalSpecs.find(updatedSpec => updatedSpec.id === localSpec.id)
            if (! updatedLocalSpec) return
            _.forEach(updatedLocalSpec, (value, key) => {
                Vue.set(localSpec, key, value)
            })
        });
        this.recalculateTotals()
    },

    async saveNow () {
        if (this.state.estimate.locked) return
        return await this.state.estimate.update()
    },

    save: debounce(function () {
        return this.saveNow()
    }, 500),

    async snapshot () {
        const estimate = await this.state.estimate.snapshot()
        window.location = estimate._links.show
    },

    repopulateNumbers () {
        let sectionNumber = 0
        let taskNumber = 0
        let itemNumber = 0

        // Repopulate sections.
        this.state.estimate.sections.forEach(section => {
            sectionNumber++
            taskNumber = 0

            // Repopulate tasks.
            section.tasks.forEach(task => {
                taskNumber++
                itemNumber = 0
                task.ref = `${sectionNumber}.${taskNumber}`
                if (task.ref === task.custom_ref) {
                    task.custom_ref = ''
                }

                // Repopulate task items.
                task.items.forEach(item => {
                    itemNumber++
                    item.ref = `${sectionNumber}.${taskNumber}.${itemNumber}`
                    if (item.ref === item.custom_ref) {
                        item.custom_ref = ''
                    }
                });
            });
        });
    },

    recalculateTotals (withItems = true) {
        const calculateTotalsFromChildren = (parent, children) => {
            const sumBy = key => _.sumBy(children ?? [], child => asNumber(child[key] ?? 0))
            parent.rate = asRoundedNumber(sumBy('rate'))
            parent.amount = asRoundedNumber(sumBy('amount'))
            parent.markup_rate = asRoundedNumber(sumBy('markup_rate'))
            parent.markup_amount = asRoundedNumber(sumBy('markup_amount'))
            parent.cf_rate = asRoundedNumber(sumBy('cf_rate'))
            parent.cf_amount = asRoundedNumber(sumBy('cf_amount'))
            parent.markup_pc = parent.amount > 0
                ? asRoundedNumber(parent.markup_amount / parent.amount * 100)
                : 0
        }

        this.state.estimate.sections.forEach(section => {
            section.tasks.forEach(task => {
                if (withItems) {
                    task.items.forEach(item => this.recalculateItem(item))
                }
                calculateTotalsFromChildren(task, task.items)
            })
            calculateTotalsFromChildren(section, section.tasks)
        })

        // Only take into account non-optional sections for the estimate.
        const nonOptionalSections = this.state.estimate.sections.filter(section => ! section.optional)
        calculateTotalsFromChildren(this.state.estimate, nonOptionalSections)

        const retentionPercent = asPositiveRoundedNumber(this.state.estimate.retention_pc / 100)
        this.state.estimate.mcd = asRoundedNumber((parseFloat(this.state.estimate.cf_amount) * parseFloat(this.state.estimate.mcd_pc)) / 100)
        this.state.estimate.total = asRoundedNumber(this.state.estimate.cf_amount - this.state.estimate.mcd)
        this.state.estimate.retention = asRoundedNumber(this.state.estimate.total * retentionPercent)
    },

    recalculateItem (item, withTotals = false) {
        // Ensure strategy and sources of truth are set.
        item.computation_strategy ??= 'cost'
        item.source_of_truth_cost ??= 'amount'
        item.source_of_truth_markup ??= 'default'
        item.source_of_truth_cf ??= 'amount'

        // Recalculate group items from their specs.
        if (item.is_group_item) {
            this.updateItemFromItsSpecs(item)
        }

        // Clean input values.
        item.rate = asRoundedNumber(item.rate)
        item.amount = asRoundedNumber(item.amount)
        item.quantity = asPositiveRoundedNumber(item.quantity)
        item.markup_rate = asRoundedNumber(item.markup_rate)
        item.markup_amount = asRoundedNumber(item.markup_amount)
        item.markup_pc = asRoundedNumber(item.markup_pc)
        item.cf_rate = asRoundedNumber(item.cf_rate)
        item.cf_amount = asRoundedNumber(item.cf_amount)

        // Ensure cost, markup and totals are valid based on the strategy.
        if (item.computation_strategy === 'cost') {
            this.recalculateItemWithCostStrategy(item)
        } else if (item.computation_strategy === 'markup') {
            this.recalculateItemWithMarkupStrategy(item)
        } else if (item.computation_strategy === 'cf') {
            this.recalculateItemWithCustomerFacingStrategy(item)
        }

        if (withTotals) {
            this.recalculateTotals(false)
        }
    },

    recalculateItemWithCostStrategy (item) {
        this.recalculateItemCustomerFacing(item)

        if (item.source_of_truth_markup === 'rate') {
            item.rate = asRoundedNumber(item.cf_rate - item.markup_rate)
            item.markup_pc = asRoundedNumber(item.rate > 0 ? item.markup_rate / item.rate * 100 : 0)
            item.amount = asRoundedNumber(item.markup_pc === -100 ? 0 : item.cf_amount / (1 + item.markup_pc / 100))
            item.markup_amount = asRoundedNumber(item.cf_amount - item.amount)
        } else if (item.source_of_truth_markup === 'amount') {
            item.amount = asRoundedNumber(item.cf_amount - item.markup_amount)
            item.markup_pc = asRoundedNumber(item.amount > 0 ? item.markup_amount / item.amount * 100 : 0)
            item.rate = asRoundedNumber(item.markup_pc === -100 ? 0 : item.cf_rate / (1 + item.markup_pc / 100))
            item.markup_rate = asRoundedNumber(item.cf_rate - item.rate)
        } else if (item.source_of_truth_markup === 'percentage') {
            item.rate = asRoundedNumber(item.markup_pc === -100 ? 0 : item.cf_rate / (1 + item.markup_pc / 100))
            item.markup_rate = asRoundedNumber(item.cf_rate - item.rate)
            item.amount = asRoundedNumber(item.markup_pc === -100 ? 0 : item.cf_amount / (1 + item.markup_pc / 100))
            item.markup_amount = asRoundedNumber(item.cf_amount - item.amount)
        } else if (item.source_of_truth_markup === 'default') {
            item.markup_pc = asRoundedNumber(this.state.estimate.default_markup_pc)
            item.rate = asRoundedNumber(item.markup_pc === -100 ? 0 : item.cf_rate / (1 + item.markup_pc / 100))
            item.markup_rate = asRoundedNumber(item.cf_rate - item.rate)
            item.amount = asRoundedNumber(item.markup_pc === -100 ? 0 : item.cf_amount / (1 + item.markup_pc / 100))
            item.markup_amount = asRoundedNumber(item.cf_amount - item.amount)
        }
    },

    recalculateItemWithMarkupStrategy (item) {
        this.recalculateItemCost(item)
        this.recalculateItemCustomerFacing(item)
        item.markup_amount = asRoundedNumber(item.cf_amount - item.amount)
        item.markup_rate = asRoundedNumber(item.cf_rate - item.rate)
        item.markup_pc = asRoundedNumber(item.amount > 0 ? item.markup_amount / item.amount * 100 : 0)
    },

    recalculateItemWithCustomerFacingStrategy (item) {
        this.recalculateItemCost(item)
        
        if (item.source_of_truth_markup === 'rate') {
            item.markup_pc = asRoundedNumber(item.rate > 0 ? item.markup_rate / item.rate * 100 : 0)
            item.markup_amount = asRoundedNumber(item.amount * item.markup_pc / 100)
        } else if (item.source_of_truth_markup === 'amount') {
            item.markup_pc = asRoundedNumber(item.amount > 0 ? item.markup_amount / item.amount * 100 : 0)
            item.markup_rate = asRoundedNumber(item.rate * item.markup_pc / 100)
        } else if (item.source_of_truth_markup === 'percentage') {
            item.markup_amount = asRoundedNumber(item.amount * item.markup_pc / 100)
            item.markup_rate = asRoundedNumber(item.rate * item.markup_pc / 100)
        } else if (item.source_of_truth_markup === 'default') {
            item.markup_pc = asRoundedNumber(this.state.estimate.default_markup_pc)
            item.markup_amount = asRoundedNumber(item.amount * item.markup_pc / 100)
            item.markup_rate = asRoundedNumber(item.rate * item.markup_pc / 100)
        }

        item.cf_rate = asRoundedNumber(item.rate + item.markup_rate)
        item.cf_amount = asRoundedNumber(item.amount + item.markup_amount)
    },

    recalculateItemCost (item) {
        if (item.source_of_truth_cost === 'rate') {
            item.amount = asRoundedNumber(item.rate * item.quantity)
        } else if (item.source_of_truth_cost === 'amount') {
            item.rate = asRoundedNumber(item.quantity > 0 ? item.amount / item.quantity : 0)
        }
    },

    recalculateItemCustomerFacing (item) {
        if (item.source_of_truth_cf === 'rate') {
            item.cf_amount = asRoundedNumber(item.cf_rate * item.quantity)
        } else if (item.source_of_truth_cf === 'amount') {
            item.cf_rate = asRoundedNumber(item.quantity > 0 ? item.cf_amount / item.quantity : 0)
        }
    },

    updateItemFromItsSpecs (item) {
        const groupDetails = this.getItemGroupDetails(item)

        if (item.computation_strategy === 'cost') {
            item.computation_strategy = 'cf'
        }

        if (item.group_use_custom_quantities && item.source_of_truth_cost === 'amount') {
            item.amount = groupDetails.amounts.total
        } else {
            item.source_of_truth_cost = 'rate'
            item.rate = groupDetails.rates.total
        }
    },

    getItemGroupDetails (item) {
        const groupWithSpecs = this.getGroupWithSpecs(item.group)
        const sumSpecs = (fn) => asRoundedNumber(sumBy(groupWithSpecs, fn))
        const sumStrategy = item.group_use_custom_quantities
            ? (fn) => sumSpecs(itemSpec => fn(itemSpec.spec) * itemSpec.quantity)
            : (fn) => sumSpecs(itemSpec => fn(itemSpec.spec))

        const rates = {
            labour_rates: sumStrategy(spec => spec.labour_cost_per_uom),
            materials: sumStrategy(spec => spec.materials_cost_per_uom),
            total: sumStrategy(spec => spec.cost_per_uom),
        }

        if (item.group_use_custom_quantities && item.source_of_truth_cost === 'amount') {
            return {
                rates: {
                    labour_rates: asRoundedNumber(item.quantity > 0 ? rates.labour_rates / item.quantity : 0),
                    materials: asRoundedNumber(item.quantity > 0 ? rates.materials / item.quantity : 0),
                    total: asRoundedNumber(item.quantity > 0 ? rates.total / item.quantity : 0),
                },
                amounts: rates,
            }
        }

        return {
            rates,
            amounts: {
                labour_rates: asRoundedNumber(rates.labour_rates * item.quantity),
                materials: asRoundedNumber(rates.materials * item.quantity),
                total: asRoundedNumber(rates.total * item.quantity),
            },
        }
    },

    getTaskGroupDetails (task) {
        return task.items.reduce((acc, item) => {
            if (! item.is_group_item) {
                acc.rates.other += item.rate
                acc.rates.total += item.rate
                acc.amounts.other += item.amount
                acc.amounts.total += item.amount
                return acc
            }

            const itemDetails = this.getItemGroupDetails(item)
            acc.rates.labour_rates += itemDetails.rates.labour_rates
            acc.rates.materials += itemDetails.rates.materials
            acc.rates.total += itemDetails.rates.total
            acc.amounts.labour_rates += itemDetails.amounts.labour_rates
            acc.amounts.materials += itemDetails.amounts.materials
            acc.amounts.total += itemDetails.amounts.total
            return acc
        }, {
            rates: {
                labour_rates: 0,
                materials: 0,
                other: 0,
                total: 0,
            },
            amounts: {
                labour_rates: 0,
                materials: 0,
                other: 0,
                total: 0,
            },
        })
    },

    addSection () {
        const newSection = {
            id: uuid(),
            key: uuid(),
            title: `Untitled Section ${this.state.estimate.sections.length + 1}`,
            tasks: [],
            order: this.state.estimate.sections.length,
            ref: null,
            custom_ref: null,
            rate: 0.00,
            amount: 0.00,
            markup_rate: 0.00,
            markup_amount: 0.00,
            markup_pc: 0.00,
            cf_rate: 0.00,
            cf_amount: 0.00,
            optional: false,
            isNew: true,
        }

        this.state.estimate.sections.push(newSection)
        this.repopulateNumbers()
        this.save()

        return newSection
    },

    duplicateSection (section) {
        const newSection = cloneDeep(section)
        newSection.id = uuid();
        newSection.key = uuid();
        newSection.isNew = true;

        const index = this.state.estimate.sections.indexOf(section)
        index >= 0 
            ? this.state.estimate.sections.splice(index + 1, 0, newSection)
            : this.state.estimate.sections.push(newSection)

        this.repopulateNumbers()
        this.recalculateTotals(false)
        this.save()

        return newSection
    },

    deleteSection (sectionToDelete) {
        this.state.estimate.sections = this.state.estimate.sections
            .filter(section => section.id !== sectionToDelete.id)

        this.repopulateNumbers()
        this.recalculateTotals(false)
        this.save()
    },

    addTask (section, name, reference = '') {
        const newTask = {
            id: uuid(),
            key: uuid(),
            order: section.tasks.length,
            ref: '',
            custom_ref: reference,
            name: name,
            rate: 0.00,
            amount: 0.00,
            markup_rate: 0.00,
            markup_amount: 0.00,
            markup_pc: 0.00,
            cf_rate: 0.00,
            cf_amount: 0.00,
            items: [],
            isNew: true
        }

        section.tasks.push(newTask)
        this.repopulateNumbers()
        this.save()

        return newTask
    },

    duplicateTask (section, task) {
        const newTask = cloneDeep(task)
        newTask.id = uuid();
        newTask.key = uuid();
        newTask.isNew = true;

        const index = section.tasks.indexOf(task)
        index >= 0 
            ? section.tasks.splice(index + 1, 0, newTask)
            : section.tasks.push(newTask)

        this.repopulateNumbers()
        this.recalculateTotals(false)
        this.save()

        return newTask
    },

    deleteTask (section, taskToDelete) {
        section.tasks = section.tasks
            .filter(task => task.id !== taskToDelete.id)

        this.repopulateNumbers()
        this.recalculateTotals(false)
        this.save()
    },

    addItem (task, payload = {}) {
        const newItem = {
            id: uuid(),
            key: uuid(),
            order: task.items.length,
            ref: '',
            custom_ref: '',
            description: '',
            quantity: 0,
            uom: '',
            rate: 0.00,
            amount: 0.00,
            markup_rate: 0.00,
            markup_amount: 0.00,
            markup_pc: 0.00,
            cf_rate: 0.00,
            cf_amount: 0.00,
            computation_strategy: 'cost',
            source_of_truth_cost: 'amount',
            source_of_truth_markup: 'default',
            source_of_truth_cf: 'amount',
            is_group_item: false,
            group: [],
            group_use_custom_quantities: false,
            isNew: true,
            ...payload,
        }

        task.items.push(newItem)
        this.repopulateNumbers()
        this.save()

        return newItem
    },

    duplicateItem (task, item) {
        const newItem = cloneDeep(item)
        newItem.id = uuid();
        newItem.key = uuid();
        newItem.isNew = true;

        const index = task.items.indexOf(item)
        index >= 0 
            ? task.items.splice(index + 1, 0, newItem)
            : task.items.push(newItem)

        this.repopulateNumbers()
        this.recalculateTotals(false)
        this.save()

        return newItem
    },

    attachSpecToItem (item, spec) {
        if (item.group.length === 0) {
            item.is_spec_item = true
            item.is_group_item = true
        }
    
        item.group.push({ quantity: 1, spec_id: spec.id })
        this.recalculateItem(item, true)
        this.save()
    },

    async duplicateAndReplaceSpecFromItem (item, itemSpec, spec) {
        const duplicatedSpec = await spec.duplicate()
        this.state.localSpecs.push(duplicatedSpec)
        itemSpec.spec_id = duplicatedSpec.id

        this.recalculateItem(item, true)
        this.save()
    },

    detachSpecFromItem (item, itemSpec, spec) {
        const itemSpecIndex = item.group.findIndex(({ spec_id }) => spec_id === itemSpec.spec_id)
        if (itemSpecIndex < 0) return
        item.group.splice(itemSpecIndex, 1)

        if (item.group.length === 0) {
            item.is_spec_item = false
            item.is_group_item = false
        }

        this.recalculateItem(item, true)
        this.save()
    },

    deleteItem (task, itemToDelete) {
        task.items = task.items
            .filter(item => item.id !== itemToDelete.id)

        this.repopulateNumbers()
        this.recalculateTotals(false)
        this.save()
    },

    getAllItems () {
        return flatMap(this.state.estimate.sections, section =>
            flatMap(section.tasks, task => task.items)
        )
    },

    getItemsUsingSpec (spec) {
        return this.getAllItems().filter(item => {
            return item.is_group_item
                && item.group.some(itemSpec => itemSpec.spec_id === spec.id)
        })
    },

    getGroupWithSpecs (group) {
        return group
            .map(itemSpec => ({
                spec: this.findLocalSpec(itemSpec.spec_id),
                ...itemSpec,
            }))
            .filter(itemSpec => !! itemSpec.spec)
    },

    findLocalSpec (specId) {
        return this.state.localSpecs.find(spec => spec.id === specId)
    },

    async deleteLocalSpec (specId) {
        await GlobalSpec.make({ id: specId }).delete()
        this.state.localSpecs = this.state.localSpecs.filter(spec => spec.id !== specId)
    },

    async copyToGlobalSpec (spec) {
        const handle = async () => {
            await spec.copyToGlobalSpec();
            
        }

        if (!spec.was_copied_to_global) {
            return handle();
        }

        modalStore.confirm({
            handle,
            title: 'This spec was already copied to the Global Spec Library',
            paragraphs: [
                'Copying it again will create a brand new Global Spec based on the current state of the local spec. Are you sure you want to proceed?',
            ],
        })
    },

    checkIfPackageStatusNeedsToBeQuoting () {
        const createdDiff = this.state.estimate.created_at_moment.diff(new Date());
        const packageStatus = this.state.estimate.package_status;
        if (packageStatus === 100 && createdDiff > -80000) {
            modalStore.confirm({
                title: 'Update package status to "Quoting"',
                paragraphs: ["Since you've created a new estimate, would you like to update the status of this package to \"Quoting\"?"],
                button: 'Yes, Update Project Status',
                handle: async () => {
                    await Http.put(route('api.v1.packages.update-status', this.state.estimate.package_id), { status_id: 200 })
                    window.location.reload();
                },
            })
        }
    },
}
